import { TFunction } from "i18next";

import {
  GENERIC_FILTER_WITHOUT_SELECT,
  ISSUES_GENERIC_FILTER,
} from "shared/filterDefinitions";
import { DataType, EventTypeEnum } from "shared/types";

import { getLastDateFilterLabelString } from "pages/Dashboard/utils";

import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import {
  extractAdvancedFilterRelationInfo,
  filterStateToFilterGroupState,
  getTopLevelRowFromFilterGroupState,
} from "features/ui/Filters/FilterBuilder/utils";
import { FilterOperator, FilterType } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";

import { ChartDateFieldEnum, CountTypeEnum } from "./constants";

export const mergeSchemas = (
  newAttributes: string[],
  schema: SchemaEntry[],
  vehiclesSchema: SchemaEntry[]
) => {
  const outSchema = [];
  for (const attribute of newAttributes) {
    const split = attribute.split(".");
    if (split.length === 1) {
      const entry = schema.find(({ accessor }) => accessor === attribute);
      if (entry) {
        outSchema.push(entry);
      }
    } else {
      // First try to find the full accessor in the main schema (for nested fields like repairCausalPartInformation.description)
      const entryInMainSchema = schema.find(
        ({ accessor }) => accessor === attribute
      );
      if (entryInMainSchema) {
        outSchema.push(entryInMainSchema);
      } else {
        // If not found, assume it's a vehicle field and look in vehiclesSchema
        const entry = vehiclesSchema.find(
          ({ accessor }) => accessor === split[1]
        );
        if (entry) {
          outSchema.push(entry);
        }
      }
    }
  }

  return outSchema;
};

export const getPeriodAttributeValueFromFilter = (
  attributes: {
    attr: string;
    addProductionSuffix: boolean;
    dateChildAttribute?: string;
  }[],
  filterState: FilterGroupState,
  t: TFunction
): { period: string; addProductionSuffix: boolean } => {
  for (const { attr, addProductionSuffix, dateChildAttribute } of attributes) {
    const foundFilter = getTopLevelRowFromFilterGroupState(attr, filterState);

    if (foundFilter && foundFilter.values.length === 1) {
      if (foundFilter.operator === FilterOperator.IN_LAST) {
        const period = getLastDateFilterLabelString(
          foundFilter.values[0],
          t
        )?.toLowerCase();

        return {
          period: period || t("schema:dashboard.selected_period"),
          addProductionSuffix,
        };
      }

      if (foundFilter.operator === FilterOperator.EXISTS) {
        const valueString = foundFilter.values[0];
        try {
          const dateAttr = dateChildAttribute ?? "exitTime";
          const dateMatch = valueString.match(
            new RegExp(`${dateAttr}=inlast:([^,\\]]+)`)
          );
          if (dateMatch && dateMatch[1]) {
            const period = getLastDateFilterLabelString(
              dateMatch[1],
              t
            )?.toLowerCase();

            return {
              period: period || t("schema:dashboard.selected_period"),
              addProductionSuffix,
            };
          }
        } catch (error) {
          console.error("Error parsing date from EXISTS filter:", error);
        }
      }
    }
  }

  return {
    period: t("schema:dashboard.selected_period"),
    addProductionSuffix: false,
  };
};

export const getChartDateFieldValue = (
  chartDateField: string,
  vehiclesFilters: FilterGroupState,
  parentAttribute: string = "lineExits",
  relationValueAttribute: string = "line",
  dateChildAttribute: string = "exitTime"
): string | undefined => {
  if (chartDateField === ChartDateFieldEnum.ADVANCED_FILTERS_DATE) {
    return extractAdvancedFilterRelationInfo(
      vehiclesFilters,
      false,
      parentAttribute,
      relationValueAttribute,
      dateChildAttribute
    );
  }

  return chartDateField;
};

export const getSmartTableChartTicks = (data: any[]) => {
  if (!data || data.length === 0) return [];

  const dates = data.map((item) => item.date);

  if (dates.length <= 3) return dates;

  const leftMost = dates[0];
  const rightMost = dates[dates.length - 1];
  const middleIndex = Math.floor(dates.length / 2);
  const middle = dates[middleIndex];

  return [leftMost, middle, rightMost];
};

export const getEventLabels = (
  eventType: EventTypeEnum,
  labelDefault: string,
  t: TFunction
) => {
  switch (eventType) {
    case EventTypeEnum.CLAIM:
      return {
        label: t("common:event_type.claim_other"),
        chartKey: "claimsChart",
      };
    case EventTypeEnum.SIGNAL_EVENT:
      return {
        label: labelDefault,
        chartKey: "signalEventsChart",
      };
    default:
      return {
        label: t("common:events"),
        chartKey: "eventsChart",
      };
  }
};

export const getYAxisKey = (
  countType: CountTypeEnum,
  eventType: EventTypeEnum
): string => {
  switch (countType) {
    case CountTypeEnum.VEHICLE_COUNT:
    case CountTypeEnum.FRC_IMPACT:
      return "numVehicles";
    case CountTypeEnum.EVENT_COUNT:
    default:
      return eventType === EventTypeEnum.CLAIM
        ? "numClaims"
        : "numSignalEventOccurrences";
  }
};

interface ProcessChartDataResult {
  processedData: any[];
  stackedBarKey: string | undefined;
}

export const processChartDataForStackedBars = (
  showOnlyEventDateData: boolean,
  filteredChartData: any[] | undefined,
  allTimeChartData: any[] | undefined,
  yAxisKey: string
): ProcessChartDataResult => {
  const chartData = showOnlyEventDateData
    ? filteredChartData
    : allTimeChartData;

  let processedData = chartData || [];
  let stackedBarKey: string | undefined = undefined;

  // when showOnlyEventDateData is false, we need to prepare stacked bar data
  // bottom bar: filteredChart values, top bar: difference (allTime - filtered)
  if (!showOnlyEventDateData && filteredChartData && allTimeChartData) {
    const filteredDataMap = new Map(
      filteredChartData.map((item) => [item.date, Number(item[yAxisKey]) || 0])
    );

    // process allTimeChartData to include both filtered and difference values
    processedData = allTimeChartData.map((item) => {
      const allTimeValue = Number(item[yAxisKey]) || 0;
      const filteredValue = filteredDataMap.get(item.date) || 0;
      const differenceValue = Math.max(0, allTimeValue - filteredValue);

      return {
        ...item,
        [yAxisKey]: filteredValue, // bottom bar (filtered data)
        [`${yAxisKey}Difference`]: differenceValue, // top bar (difference)
      };
    });

    stackedBarKey = `${yAxisKey}Difference`;
  }

  return { processedData, stackedBarKey };
};

export const replaceAdvancedFilterDateRange = (
  filterGroup: FilterGroupState<FilterRowState>,
  newTimeValue: string,
  dateAttribute: string = "exitTime",
  parentAttribute: string = "lineExits"
): FilterGroupState<FilterRowState> => {
  const replaceInChildren = (
    children: (FilterRowState | FilterGroupState<FilterRowState>)[]
  ): (FilterRowState | FilterGroupState<FilterRowState>)[] =>
    children.map((child) => {
      if (child.type === "group") {
        return {
          ...child,
          children: replaceInChildren(child.children),
        };
      }

      if (child.type === "row" && child.attribute === parentAttribute) {
        if (!child.values || child.values.length === 0) {
          return child;
        }

        const valueString = child.values[0];

        try {
          // remove outer brackets if present
          const cleanedValue = valueString.replace(/^\[|\]$/g, "");

          const parts = cleanedValue.split(",");

          if (parts.length < 1) {
            return child;
          }

          // parse all parts and find the one matching dateAttribute
          const updatedParts = parts.map((part) => {
            const attrMatch = part.match(/^([^=]+)=/);
            if (!attrMatch) {
              return part;
            }

            const attrName = attrMatch[1];

            if (attrName === dateAttribute) {
              return `${attrName}=${newTimeValue}`;
            }

            return part;
          });

          const newValue = `[${updatedParts.join(",")}]`;

          return {
            ...child,
            values: [newValue],
          };
        } catch (error) {
          console.error("Error replacing advanced filter date value:", error);

          return child;
        }
      }

      return child;
    });

  return {
    ...filterGroup,
    children: replaceInChildren(filterGroup.children),
  };
};

export const calculateAggregatedMetric = (
  data: any[] | undefined,
  metricField: string
): number | null => {
  if (!data || data.length === 0) return null;

  if (metricField === "FRCPercent") {
    let totalNumVehicles = 0;
    let totalTotalVehicles = 0;

    for (const item of data) {
      const numVehicles = item.numVehicles as number;
      const totalVehicles = item.totalVehicles as number;

      if (numVehicles != null) {
        totalNumVehicles += numVehicles;
      }

      if (totalVehicles != null) {
        totalTotalVehicles += totalVehicles;
      }
    }

    if (totalTotalVehicles === 0) return null;

    return 100 * (1 - totalNumVehicles / totalTotalVehicles);
  }

  if (metricField === "eventsPerUnit") {
    let totalNumEvents = 0;
    let totalTotalVehicles = 0;

    for (const item of data) {
      const numEvents = item.numEvents as number;
      const totalVehicles = item.totalVehicles as number;

      if (numEvents != null) {
        totalNumEvents += numEvents;
      }

      if (totalVehicles != null) {
        totalTotalVehicles += totalVehicles;
      }
    }

    if (totalTotalVehicles === 0) return null;

    return totalNumEvents / totalTotalVehicles;
  }

  let sum = 0;
  for (const item of data) {
    const value = item[metricField] as number;
    if (value != null) {
      sum += value;
    }
  }

  return sum;
};

// Scopes a filter's value-options query to the current user's own investigations, so
// the Name "is any of" dropdown lists only their projects rather than every user's.
const createdByFilter = (createdBy: string): FilterGroupState =>
  filterStateToFilterGroupState({
    createdBy: { operator: FilterOperator.EQUALS, values: [createdBy] },
  });

export const getMyInvestigationsSchema = (
  createdBy: string,
  t: TFunction
): SchemaEntry[] => [
  {
    accessor: "name",
    label: t("common:name"),
    dataType: DataType.STRING,
    flex: 1,
    description: t("schema:dashboard.my_projects_name_description"),
    filter: ISSUES_GENERIC_FILTER({
      label: t("common:name"),
      fieldName: "name",
      filterType: FilterType.STRING,
      search: true,
      customFilter: createdByFilter(createdBy),
    }),
  },
  {
    accessor: "updatedAt",
    label: t("schema:dashboard.my_projects_last_saved"),
    dataType: DataType.DATE_WITH_TIME_UTC,
    flex: 1,
    description: t("schema:dashboard.my_projects_last_saved_description"),
    filter: {
      label: t("schema:dashboard.my_projects_last_saved"),
      fieldName: "updatedAt",
      filterType: FilterType.DATE,
      filterDataType: DataType.DATE_WITH_TIME_UTC,
      disableSelectFilters: true,
    },
  },
  {
    accessor: "smartGroup.externalID",
    label: t("schema:project_id"),
    dataType: DataType.STRING,
    flex: 1,
    description: t("schema:dashboard.my_projects_project_id_description"),
    // externalID is filterable but not sortable on the issues API.
    sortable: false,
    filter: GENERIC_FILTER_WITHOUT_SELECT({
      label: t("schema:project_id"),
      fieldName: "smartGroup.externalID",
      filterType: FilterType.STRING,
    }),
  },
  {
    accessor: "actions",
    label: t("common:actions"),
    dataType: DataType.JSX,
    flex: 1,
    description: t("common:actions"),
    sortable: false,
  },
];

export const MY_INVESTIGATIONS_ATTRIBUTE_ACCESSORS = getMyInvestigationsSchema(
  "",
  ((key: string) => key) as TFunction
)
  .filter(({ filter }) => Boolean(filter))
  .map(({ accessor, filter }) => filter?.fieldName || accessor);
